/**
 * "Für Orgas & Con-Teams" page copy — real content from the org's own
 * design project, not fabricated. One correction made against our own
 * already-published legal facts: the design draft said "gemeinnützige
 * gGmbH" in the Fördermittel card, but the real, notarized legal form is
 * "gUG (haftungsbeschränkt)" (Amtsgericht Dresden, HRB 48120 — see
 * Gesellschaftsvertrag/Impressum). Fixed to gUG.
 *
 * The design's "Bisher gemeinsam umgesetzt" case-study cards used
 * illustrative example names/numbers (fictional partner orgas, invented
 * attendance figures) — same category as the placeholder team-member
 * names dropped elsewhere on this site. Not published as real. The
 * section now instead renders real events flagged `externalOrga: true`
 * in content/*\/events/*.md (see getExternalOrgaEvents in utils/content.js)
 * — caseStudiesEmpty is the fallback shown only while none exist. Three
 * of the four FAQ answers weren't written in the source design (shown
 * collapsed, no answer text) — kept as placeholders rather than invented.
 */
export const ORGAS_CONTENT = {
  de: {
    hero: {
      eyebrow: "Für Orgas & Con-Teams",
      metaTitle: "Trägerschaft für LARP-Events: Für Orgas",
      metaDescription: "Du willst ein LARP oder eine Con veranstalten, aber nicht haften, Verträge schließen und Buchhaltung machen? Tales&Legends übernimmt den Rahmen – gemeinnützig.",
      title: "Du hast die Idee. Wir bringen den Rahmen mit.",
      imageAlt: "Person in rotem Gewand schreibt mit einer Feder, im Hintergrund eine Ritterrüstung",
      body: "Viele Cons scheitern nicht an der Idee, sondern an Haftung, Verträgen, Versicherung und Vorkasse. Wir treten als gemeinnütziger Träger auf, beraten euch von der Konzeption bis zur Abrechnung — und beantworten alle eure Fragen. Viele gute LARPs scheitern nicht an der Idee, sondern an Haftung, Vorkasse und Papierkram. Wer ein LARP veranstalten will, ohne einen eigenen Verein zu gründen, findet bei uns den Rahmen: Wir sind der Veranstalter auf dem Papier, du bleibst es im Spiel.",
      ctaPrimary: "Idee vorstellen",
      ctaSecondary: "Wer wir sind",
    },
    servicesTitle: "Was wir übernehmen",
    services: [
      { title: "Trägerschaft", body: "Wir sind Veranstalter im rechtlichen Sinn: Verträge, Haftpflicht, GEMA, Jugendschutz, Genehmigungen. Ihr bleibt inhaltlich in der Verantwortung." },
      { title: "Beratung", body: "Kalkulation, Ticketpreise, Awareness-Konzept, Zeitplan, Anmeldesystem. Wir gehen euer Konzept durch und sagen, wo es kippt." },
      { title: "Netzwerk", body: "Wir vermitteln euch bei Bedarf Helfer:innen aus unserem Netzwerk und sind teilweise auch selbst vor Ort dabei." },
      { title: "Finanzen", body: "Konto, Buchhaltung, Vorkasse für Location und Material. Ihr müsst nicht privat in Vorleistung gehen." },
      { title: "Fördermittel", body: "Als gemeinnützige gUG können wir Anträge stellen, die einer losen Orga-Gruppe verschlossen bleiben — inklusive Verwendungsnachweis." },
      { title: "Reichweite", body: "Wir bewerben euer Event über unsere Website, Social Media und unseren Newsletter — für mehr Sichtbarkeit." },
    ],
    processTitle: "Wie eine Zusammenarbeit läuft",
    steps: [
      { num: "01", title: "Erstgespräch", body: "Ihr erzählt, was ihr vorhabt. Wir sagen ehrlich, ob wir passen — und ob die Idee in der Form tragfähig ist.", duration: "unverbindlich, 1 Stunde" },
      { num: "02", title: "Konzept & Kalkulation", body: "Gemeinsam in zwei bis drei Terminen: Ablauf, Teilnehmerzahl, Budget, Risiken. Am Ende steht ein Papier, mit dem man Locations anfragen kann.", duration: "4–8 Wochen" },
      { num: "03", title: "Trägervereinbarung", body: "Schriftlich festgehalten, wer was entscheidet und wer welches Risiko trägt. Inhalt bleibt bei euch, Recht und Geld bei uns.", duration: "ein Termin" },
      { num: "04", title: "Umsetzung", body: "Wir unterstützen bei Themen wie Anmeldung, Miete und mehr. Teilweise können wir auch selbst vor Ort sein oder Helfer:innen aus unserem Netzwerk für euch akquirieren. Auf jeden Fall informieren wir über unsere Website, Social Media und Newsletter über euer Event.", duration: "bis zum Con" },
      { num: "05", title: "Abrechnung & Auswertung", body: "Offene Bücher, Verwendungsnachweis, Rückblick mit dem Team. Überschüsse fließen in den Freiplatz-Fonds, den Betrieb oder in eure nächste Con.", duration: "6 Wochen danach" },
    ],
    fitTitle: "Passt gut, wenn …",
    fitItems: [
      "ihr eine ausgearbeitete Idee habt, aber keinen Verein im Rücken",
      "der Con offen zugänglich ist",
      "ihr Awareness und Sicherheit ernst nehmt",
      "mindestens etwa sechs Monate (+/- ein paar Wochen) bis zum Termin bleiben",
    ],
    notFitTitle: "Passt eher nicht, wenn …",
    notFitItems: [
      "der Con rein kommerziell gedacht ist",
      "ihr nur eine Rechnungsadresse sucht",
      "Inhalte diskriminierend oder ausgrenzend sind",
      "der Termin in unter drei Monaten liegt",
    ],
    caseStudiesTitle: "Bisher gemeinsam umgesetzt",
    caseStudiesEmpty: "Wir sind noch jung: Echte Kooperationen zeigen wir hier, sobald es sie gibt.",
    caseStudyWith: "mit",
    faqTitle: "Häufige Fragen von Orgas",
    faq: [
      {
        category: "Trägerschaft & Verantwortung",
        items: [
          {
            q: "Was bedeutet „Trägerschaft” bei Tales&Legends konkret?",
            a: "Als Träger übernehmen wir die rechtliche und finanzielle Hülle für dein Event: Ticketverkauf, Rechnungsstellung und Spenden laufen unter unserem gemeinnützigen Dach. Du organisierst das Event — wir stellen den Rahmen, der das möglich macht, ohne dass du selbst eine Organisation gründen musst.",
          },
          {
            q: "Behalte ich die kreative Kontrolle über mein Event?",
            a: "Ja, vollständig. Tales&Legends ist dein rechtlicher und finanzieller Rahmen — nicht dein Co-Veranstalter. Programm, Konzept, Atmosphäre und alle inhaltlichen Entscheidungen liegen bei dir.",
          },
          {
            q: "Wem gehört die Idee am Ende?",
            a: "Dir. Tales&Legends erwirbt durch die Trägerschaft keinerlei Rechte an deinem Konzept, deinem Namen oder deinen Inhalten. Was du einbringst, bleibt deins.",
          },
          {
            q: "Wer entscheidet im Streitfall?",
            a: "Wir setzen auf klare Absprachen im Vorfeld, um Konflikte gar nicht erst entstehen zu lassen. Sollte es dennoch Uneinigkeit geben, suchen wir zuerst das direkte Gespräch. In Fragen, die den gemeinnützigen Status oder finanzielle Pflichten betreffen, hat Tales&Legends das letzte Wort — das ist rechtlich nicht anders möglich.",
          },
          {
            q: "Brauche ich selbst eine gemeinnützige Rechtsform?",
            a: "Nein — das ist sogar der Hauptgrund, warum Trägerschaft sinnvoll sein kann. Du kannst als Einzelperson, informelle Gruppe oder privat organisiertes Team anfragen. Tales&Legends stellt den rechtlichen Rahmen bereit.",
          },
        ],
      },
      {
        category: "Kosten & Finanzen",
        items: [
          {
            q: "Was kostet das?",
            a: "Beratung und Erstgespräch sind kostenfrei. Bei Trägerschaft behalten wir einen Verwaltungsanteil von 8 % des Ticketpreises — damit halten wir den Betrieb am Laufen.\n\nWas am Ende übrig bleibt, fließt nicht zurück als Auszahlung, sondern bleibt im Kreislauf: in den Freiplatz-Fonds, in andere Events oder in euer nächstes Projekt. Das Geld arbeitet weiter — für die Community, nicht für Konten.",
          },
          {
            q: "Was passiert bei einem Defizit?",
            a: "Das versuchen wir gemeinsam gar nicht erst so weit kommen zu lassen. Wir planen von Anfang an so, dass das Budget aufgeht — und bleiben während der gesamten Vorbereitung mit euch im Gespräch, um frühzeitig Lösungen zu finden, wenn sich etwas verschiebt.\n\nSollte es dennoch eng werden, lassen wir euch nicht allein. Wir schauen gemeinsam, was möglich ist — und im Notfall bringen wir auch selbst Kapital ein.",
          },
          {
            q: "Wie und wann wird abgerechnet?",
            a: "Die genaue Abrechnungsweise legen wir gemeinsam im Vorfeld fest. In der Regel erfolgt die Abrechnung nach dem Event auf Basis der tatsächlichen Einnahmen, abzüglich des Verwaltungsanteils und der vereinbarten Kosten.\n\nWichtig zu wissen: Wenn euer Event über Förderungen finanziert wird, gelten dafür oft feste Fristen und Nachweispflichten. Die behalten wir gemeinsam im Blick — meldet euch frühzeitig, damit wir alles rechtzeitig in die Wege leiten können.",
          },
        ],
      },
      {
        category: "Ablauf & Einstieg",
        items: [
          {
            q: "Welche Art von Events kann Tales&Legends unterstützen?",
            a: "Wir unterstützen vor allem LARP-Events in ganz Deutschland — solange sie einen offenen und gemeinnützigen Charakter haben. Genre, Größe und Ort spielen dabei keine Rolle. Ob dein Event passt, klären wir am schnellsten im kurzen Erstgespräch.",
          },
          {
            q: "Muss unser Event in Sachsen stattfinden?",
            a: "Nein. Wir unterstützen Events in ganz Deutschland, nicht nur in Sachsen — der Ort spielt für die Trägerschaft keine Rolle.",
          },
          {
            q: "Wie lange dauert es, bis eine Zusammenarbeit startet?",
            a: "Nach dem Erstgespräch und einer kurzen Prüfung des Konzepts dauert der Einstieg in der Regel wenige Wochen. Je früher du dich meldest, desto entspannter wird die Planung.",
          },
          {
            q: "Welche Unterlagen oder Infos brauche ich für den ersten Schritt?",
            a: "Keine formalen Unterlagen. Eine grobe Beschreibung deines Events, einen ungefähren Zeitplan und eine Vorstellung der erwarteten Einnahmen und Ausgaben reichen völlig aus — und selbst wenn du davon noch nicht alles beisammenhast, ist das kein Problem. Schick uns einfach, was du schon weißt. Den Rest klären wir gemeinsam im Erstgespräch.",
          },
          {
            q: "Kann ich Tales&Legends auch nur für einzelne Aspekte nutzen — z. B. nur den Freiplatz-Fonds?",
            a: "Nicht jede Zusammenarbeit muss eine vollständige Trägerschaft sein — das besprechen wir gerne individuell.\n\nDen Freiplatz-Fonds direkt zu nutzen ist aktuell allerdings nur für unsere eigenen Events möglich. Aber auch dafür suchen wir gemeinsam nach einer Lösung: Eine Möglichkeit, die wir bei Bedarf einrichten können, ist ein optionales Zweitticket im Buchungssystem — damit können Besucher bewusst einen Freiplatz für jemand anderen mitfinanzieren. Meld dich einfach, wir schauen, was passt.",
          },
          {
            q: "Was passiert, wenn ich die Zusammenarbeit beenden möchte?",
            a: "Kein Problem. Laufende Verpflichtungen — etwa bereits verkaufte Tickets oder offene Rechnungen — müssen natürlich gemeinsam geregelt werden. Darüber hinaus gibt es keine Bindung. Wie das im Detail aussieht, halten wir im Vorfeld schriftlich fest.",
          },
        ],
      },
    ],
    ctaTitle: "Erzähl uns von eurer Con",
    ctaBody: "Ein Absatz reicht: Idee, ungefährer Termin, Teilnehmerzahl, was euch fehlt. Wir melden uns so schnell wie möglich.",
    ctaButton: "Idee vorstellen",
  },
  en: {
    hero: {
      eyebrow: "For Organizers & Con Teams",
      metaTitle: "Legal Sponsorship for LARP Events",
      metaDescription: "Want to run a LARP or con but not deal with liability, contracts, and bookkeeping? Tales&Legends provides the legal framework – as a non-profit.",
      title: "You have the idea. We bring the framework.",
      imageAlt: "A person in a red robe writing with a quill, a suit of plate armour behind them",
      body: "Many cons don't fail because of the idea — they fail on liability, contracts, insurance, and upfront costs. We act as your non-profit legal sponsor, advising you from concept to final accounting — and answering all your questions. Many good LARPs don't fail on the idea, but on liability, upfront costs, and paperwork. If you want to run a LARP without founding your own association, we provide the framework: we're the organiser of record on paper, you stay the organiser in the game.",
      ctaPrimary: "Pitch your idea",
      ctaSecondary: "Who we are",
    },
    servicesTitle: "What we take on",
    services: [
      { title: "Legal sponsorship", body: "We're the legal organizer of record: contracts, liability insurance, music licensing, youth protection, permits. You stay in charge of the content." },
      { title: "Consulting", body: "Budgeting, ticket pricing, awareness concept, timeline, registration system. We go through your concept and tell you where it breaks." },
      { title: "Network", body: "If needed, we connect you with helpers from our network and are sometimes on site ourselves too." },
      { title: "Finances", body: "Bank account, bookkeeping, upfront payment for venue and materials. You don't have to front the money yourselves." },
      { title: "Grants & funding", body: "As a registered non-profit (gUG), we can apply for grants that stay closed to an informal group of organizers — including the required usage reports." },
      { title: "Reach", body: "We promote your event through our website, social media, and newsletter — for more visibility." },
    ],
    processTitle: "How a collaboration works",
    steps: [
      { num: "01", title: "First conversation", body: "You tell us what you're planning. We tell you honestly whether we're a fit — and whether the idea is workable in that form.", duration: "no obligation, 1 hour" },
      { num: "02", title: "Concept & budget", body: "Together, over two to three sessions: schedule, headcount, budget, risks. You end up with a document you can use to inquire about venues.", duration: "4–8 weeks" },
      { num: "03", title: "Sponsorship agreement", body: "Written down: who decides what, and who carries which risk. Content stays yours — legal and financial responsibility is ours.", duration: "one meeting" },
      { num: "04", title: "Execution", body: "We support you with topics like registration, venue rental, and more. Sometimes we can also be on site ourselves, or bring in helpers from our network for you. Either way, we'll promote your event through our website, social media, and newsletter.", duration: "until the con" },
      { num: "05", title: "Accounting & review", body: "Open books, usage report, a debrief with the team. Any surplus goes into the free-ticket fund, our operations, or your next con.", duration: "6 weeks after" },
    ],
    fitTitle: "A good fit when …",
    fitItems: [
      "you have a worked-out idea but no association behind you",
      "the con is open to the public",
      "you take awareness and safety seriously",
      "you have roughly six months (give or take a few weeks) until the event",
    ],
    notFitTitle: "Probably not a fit when …",
    notFitItems: [
      "the con is purely commercial",
      "you're just looking for a billing address",
      "the content is discriminatory or exclusionary",
      "the date is less than three months away",
    ],
    caseStudiesTitle: "Collaborations so far",
    caseStudiesEmpty: "We're still young: real collaborations will show up here once they exist.",
    caseStudyWith: "with",
    faqTitle: "Common questions from organizers",
    faq: [
      {
        category: "Sponsorship & Control",
        items: [
          {
            q: "What does \"sponsorship\" mean at Tales&Legends specifically?",
            a: "As your sponsor, we take on the legal and financial shell for your event: ticket sales, invoicing, and donations run under our non-profit umbrella. You organize the event — we provide the framework that makes it possible, without you having to found your own organization.",
          },
          {
            q: "Do I keep creative control over my event?",
            a: "Yes, completely. Tales&Legends is your legal and financial framework — not your co-organizer. Program, concept, atmosphere, and all content decisions are yours.",
          },
          {
            q: "Who owns the idea in the end?",
            a: "You do. Through the sponsorship, Tales&Legends acquires no rights to your concept, your name, or your content. What you bring stays yours.",
          },
          {
            q: "Who decides in case of a dispute?",
            a: "We rely on clear agreements up front, so conflicts don't arise in the first place. If disagreement does come up, we look for a direct conversation first. On questions concerning our non-profit status or financial obligations, Tales&Legends has the final say — that's simply how it has to work legally.",
          },
          {
            q: "Do I need my own non-profit legal form?",
            a: "No — that's actually the main reason sponsorship can make sense. You can apply as an individual, an informal group, or a privately organized team. Tales&Legends provides the legal framework.",
          },
        ],
      },
      {
        category: "Cost & Finances",
        items: [
          {
            q: "What does it cost?",
            a: "Consulting and the first conversation are free. Under sponsorship, we keep an administrative share of 8% of the ticket price — that's what keeps our operations running.\n\nWhatever's left over doesn't get paid out — it stays in circulation: into the free-ticket fund, into other events, or into your next project. The money keeps working — for the community, not for bank accounts.",
          },
          {
            q: "What happens in case of a deficit?",
            a: "Together, we try not to let it get that far in the first place. We plan the budget from the start so it works out — and stay in touch with you throughout preparation, so we can find solutions early if something shifts.\n\nIf things do get tight, we won't leave you on your own. We'll look together at what's possible — and in an emergency, we'll also contribute capital ourselves.",
          },
          {
            q: "How and when is it settled?",
            a: "We agree on the exact settlement method together beforehand. Usually, settlement happens after the event, based on actual revenue, minus the administrative share and agreed costs.\n\nGood to know: if your event is financed through grants, those often come with fixed deadlines and reporting requirements. We keep an eye on those together — reach out early so we can get everything moving in time.",
          },
        ],
      },
      {
        category: "Process & Getting Started",
        items: [
          {
            q: "What kinds of events can Tales&Legends support?",
            a: "We mainly support LARP events across Germany — as long as they're open to the public and have a non-profit character. Genre, size, and location don't matter. The fastest way to find out if your event fits is a short first conversation.",
          },
          {
            q: "Does our event have to take place in Saxony?",
            a: "No. We support events across all of Germany, not just Saxony — location doesn't matter for the sponsorship.",
          },
          {
            q: "How long does it take for a collaboration to start?",
            a: "After the first conversation and a brief review of the concept, getting started usually takes a few weeks. The earlier you reach out, the more relaxed the planning.",
          },
          {
            q: "What documents or information do I need for the first step?",
            a: "No formal documents. A rough description of your event, an approximate timeline, and an idea of expected income and expenses are completely enough — and even if you don't have all of that together yet, that's fine. Just send us what you already know. We'll figure out the rest together in the first conversation.",
          },
          {
            q: "Can I use Tales&Legends for just individual aspects — e.g. only the free-ticket fund?",
            a: "Not every collaboration has to be a full sponsorship — we're happy to discuss that individually.\n\nUsing the free-ticket fund directly is currently only possible for our own events. But we're looking for a solution here too: one option we can set up if needed is an optional second ticket in the booking system, letting attendees deliberately co-fund a free spot for someone else. Just reach out and we'll see what fits.",
          },
          {
            q: "What happens if I want to end the collaboration?",
            a: "No problem. Ongoing obligations — such as tickets already sold or outstanding invoices — obviously need to be settled together. Beyond that, there's no further commitment. We put the details of that in writing beforehand.",
          },
        ],
      },
    ],
    ctaTitle: "Tell us about your con",
    ctaBody: "One paragraph is enough: idea, rough date, expected headcount, what you're missing. We'll get back to you as soon as possible.",
    ctaButton: "Pitch your idea",
  },
};
